//format price to 2 decimal places

const CURRENCY_SYMBOL = "₹";

export function formatPriceWithoutSymbol(
  price: number | string | undefined
): string | null {
  if (!price) return null;

  const numericPrice = Number(price);

  if (numericPrice.toString().includes(".")) {
    const formattedPrice = numericPrice.toFixed(2);
    return `${parseFloat(formattedPrice).toLocaleString()}`;
  }

  if (isNaN(numericPrice)) return null;

  return `${numericPrice.toLocaleString()}`;
}

export function formatPrice(price: number | string): string | null {
  if (!price) return null;

  const priceString = formatPriceWithoutSymbol(price);

  return priceString ? `${CURRENCY_SYMBOL}${priceString}` : null;
}

/**
 * Converts a timestamp to relative time (e.g., "2 mins ago", "1 hour ago")
 * @param timestamp - The timestamp to convert
 * @returns Relative time string
 */
export function getRelativeTime(timestamp: string): string {
  const now = new Date().getTime();
  const orderTime = new Date(timestamp).getTime();
  const diffInMinutes = Math.round((now - orderTime) / (1000 * 60));

  if (diffInMinutes < 1) {
    return "Just now";
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} ${diffInMinutes === 1 ? "min" : "mins"} ago`;
  } else if (diffInMinutes < 1440) {
    // Less than 24 hours
    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;
    if (minutes === 0) {
      return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
    } else {
      return `${hours} ${hours === 1 ? "hour" : "hours"} ${minutes} ${minutes === 1 ? "min" : "mins"} ago`;
    }
  } else {
    const days = Math.floor(diffInMinutes / 1440);
    return `${days} ${days === 1 ? "day" : "days"} ago`;
  }
}

// --- Paise helpers -------------------------------------------------------
// Subscription plan amounts are stored and transmitted in paise with at most
// two decimal places of rupee precision. Keeping the conversion in one place
// avoids float drift from ad-hoc `/ 100` and `* 100` at call sites.

const PAISE_IN_RUPEE = 100;

/** Paise -> rupees, e.g. 471882 -> 4718.82 */
export function paiseToRupees(paise: number): number {
  return Math.round(paise) / PAISE_IN_RUPEE;
}

/**
 * Rupees -> paise, e.g. 4718.82 -> 471882. Rounds so that float artifacts
 * (4718.82 * 100 === 471882.00000000006) never reach the API.
 */
export function rupeesToPaise(rupees: number): number {
  return Math.round(rupees * PAISE_IN_RUPEE);
}

/**
 * Sanitises raw rupee text as it is typed, hard-capping precision at two
 * decimals. Extra digits are discarded rather than rejected afterwards, so a
 * third decimal can never land in the field in the first place — including
 * via paste.
 *
 * Returns a string so a trailing "." survives while the user is mid-typing;
 * convert with `rupeesToPaise(Number(value))` on submit.
 */
export function clampRupeeInput(raw: string): string {
  if (raw === null || raw === undefined) return "";

  // Drop anything that is not a digit or a decimal point.
  let cleaned = String(raw).replace(/[^\d.]/g, "");

  // Collapse any additional decimal points into the first one.
  const firstDot = cleaned.indexOf(".");
  if (firstDot !== -1) {
    cleaned =
      cleaned.slice(0, firstDot + 1) +
      cleaned.slice(firstDot + 1).replace(/\./g, "");
  }

  const [whole, fraction] = cleaned.split(".");
  if (fraction === undefined) return whole;

  return `${whole}.${fraction.slice(0, 2)}`;
}

/**
 * Formats a paise amount for display, always with two decimals so plan
 * amounts line up in a table. Returns a dash for missing values.
 */
export function formatPaise(paise: number | null | undefined): string {
  if (paise === null || paise === undefined || Number.isNaN(Number(paise))) {
    return "—";
  }

  const rupees = paiseToRupees(Number(paise));

  return `${CURRENCY_SYMBOL}${rupees.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
